import { useEffect, useRef } from 'react';

/**
 * Invoke `handler` synchronously when the page goes away. The latest handler is
 * always used (no stale closure) and the listeners are attached once for the
 * lifetime of the consuming component.
 *
 * Both 'beforeunload' and 'pagehide' are observed because neither is sufficient:
 * mobile Safari and bfcache navigations skip 'beforeunload' entirely. The handler
 * runs at most once, so a browser firing both still gets a single teardown.
 */
export const useBeforeUnload = (handler: () => void): void => {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    let handled = false;
    const onUnload = () => {
      if (handled) return;
      handled = true;
      handlerRef.current();
    };

    window.addEventListener('beforeunload', onUnload);
    window.addEventListener('pagehide', onUnload);
    return () => {
      window.removeEventListener('beforeunload', onUnload);
      window.removeEventListener('pagehide', onUnload);
    };
  }, []);
};
